import { usePrevious } from '@react-hookz/web';
import {
  ExpandableNavigationVariant,
  NavigationItemDirection,
  NavigationResolution,
  type UrlNavItem,
} from '@unifyapps/defs/types/interface';
import _filter from 'lodash/filter';
import { type CSSProperties, useEffect, useMemo, useState } from 'react';
import { EMPTY_ARRAY, EMPTY_OBJECT } from '../../../../../consts/empty';
import { useDrawer } from '../../../../Drawer';
import { UA_PLATFORM_SIDE_NAV_OVERLAPPING_STYLES } from '../../../components/platform/constants';
import {
  isHorizontalUpfrontFn,
  isLabelVariantExpandable,
  isPlatformSideNav as isPlatformSideNavHelper,
  isTopNavigation,
} from '../../../helpers';
import { useGetSimplifiedNavigation } from '../../../hooks/useGetSimplifiedNavigation';
import type {
  NavItems,
  NavigationItemAppearance,
  NavigationItemLabelVariantEnum,
  NavigationStylesType,
  SideNavProps,
} from '../../../types';
import { getActivePathnames } from '../../../utils';
import { removeUnsupportedItemsFromGroup } from '../../../utils/navGroup';
import useAppSideNavState from './useAppSideNavState';
import { useOverlapObserver } from './useOverlapObserver';
import { useNavItemPopover } from './useNavItemPopover';

/**
 * NAVIGATION SCROLLBAR STYLING - PLATFORM SIDENAV
 *
 * Platform scrollbars are intentionally hidden. Spacing is handled by symmetric
 * container padding in SideNavigation, so we must not reserve a scrollbar gutter
 * or add browser-specific right-side compensation.
 */

const getIsDisableHoverListeners = (
  labelVariant: NavigationItemLabelVariantEnum,
  disableHoverListeners?: boolean,
) => {
  return Boolean(
    disableHoverListeners ||
    (!isPlatformSideNavHelper(labelVariant) && !isLabelVariantExpandable(labelVariant)),
  );
};

export function useSideNavigation(props: SideNavProps) {
  const {
    disableHoverListeners,
    controlledState,
    navigationClassNames,
    navigationProps,
    navigationStyles,
    pathname,
    projectSlug,
  } = props;

  const {
    navigationItemAppearance,
    navigation: _navigation,
    theme,
    position,
    app,
    expandableNavVariant = ExpandableNavigationVariant.SM,
    resolution = NavigationResolution.XL,
  } = navigationProps;

  const prevPathName = usePrevious(pathname);

  const { hide: hideDrawer, isOpen: isDrawerOpen } = useDrawer();

  const { className, borderColorClassName } = navigationClassNames;
  const { style, navItemStyles, navItemsGapStyles, navItemBorderRadiusStyles } =
    navigationStyles ?? (EMPTY_OBJECT as NavigationStylesType);
  const {
    rootClassName,
    typographyClassName,
    navItemsGapClassName,
    navItemClassName,
    navItemBorderRadiusClassName,
  } = navigationClassNames;

  const { labelVariant, selectionVariant, alignment, direction, icon } =
    navigationItemAppearance ?? (EMPTY_OBJECT as NavigationItemAppearance);
  const isPlatformSideNav = isPlatformSideNavHelper(labelVariant);

  const isLabelExpandable = isLabelVariantExpandable(labelVariant);
  const isHorizontalUpfront = isHorizontalUpfrontFn(labelVariant, selectionVariant, direction);
  const isTopPositioned = isTopNavigation(position);

  const [expandedGroupId, setExpandedGroupId] = useState<string | null>(null);

  // single shared open-popover slot for the collapsed rail (hover + pin). See useNavItemPopover.
  const { navItemPopover, setNavItemPopover, closeNavItemPopover } = useNavItemPopover();

  const { expanded, activeMenuItemId, setActiveMenuItemId, hoverProps, setExpanded } =
    useAppSideNavState({
      // platform nav expands on the header click toggle, not on hover, so suppress the whole-nav
      // hover listeners. Per-item L2 flyout hover is handled in NavigationGroup, not here.
      disableHoverListeners:
        getIsDisableHoverListeners(labelVariant, disableHoverListeners) || isPlatformSideNav,
      isPlatformSideNav,
      controlledState,
      setExpandedGroupId,
      expanded: props.expanded,
      onExpandedChange: props.onExpandedChange,
    });

  const activePathnames = useMemo(
    () => getActivePathnames(pathname, projectSlug),
    [pathname, projectSlug],
  );

  // To hide the navigation drawer when the pathname changes and the drawer is open
  useEffect(() => {
    if (prevPathName === pathname) return;

    // a collapsed-rail popover closes once navigation happens (e.g. clicking a popover row).
    closeNavItemPopover();

    // platform nav is click-controlled, so its expanded state persists across navigation; only
    // hover-controlled navs auto-collapse on route change.
    if (!isPlatformSideNav) setExpanded(false);

    if (isDrawerOpen) {
      setActiveMenuItemId(null);
      hideDrawer?.();
    }
  }, [
    prevPathName,
    pathname,
    setActiveMenuItemId,
    setExpanded,
    hideDrawer,
    isDrawerOpen,
    isPlatformSideNav,
    closeNavItemPopover,
  ]);

  // the popover only exists on the collapsed rail; clear it when the rail expands so a stale
  // open slot can't auto-open a popover the next time the rail collapses.
  useEffect(() => {
    if (expanded) {
      closeNavItemPopover();
    }
  }, [expanded, closeNavItemPopover]);

  const navigation = useGetSimplifiedNavigation(_navigation, labelVariant);

  const isUserNavItemPresent = useMemo(
    () => navigation.secondary?.some((item) => item.type === 'USER_MENU'),
    [navigation.secondary],
  );

  const brandNavigationItem = useMemo(
    () =>
      navigation.primary.find((item) => item.id === 'BRAND_NAVIGATION') as UrlNavItem | undefined,
    [navigation.primary],
  );

  const otherPrimaryNavItems = useMemo(
    () => navigation.primary.filter((item) => item.id !== 'BRAND_NAVIGATION'),
    [navigation.primary],
  );

  // If labelVariant is not Expandable on hover, then we do not show
  // navigation items of type GROUP
  // For top navigation, we do not show navigation items of type GROUP
  const adaptedPrimaryNavItems = useMemo(() => {
    const updatedNavItems =
      (!isLabelExpandable || isTopPositioned) && !isPlatformSideNav
        ? _filter(otherPrimaryNavItems, (item) => item.type !== 'GROUP')
        : otherPrimaryNavItems;

    return removeUnsupportedItemsFromGroup(updatedNavItems);
  }, [isLabelExpandable, otherPrimaryNavItems, isTopPositioned, isPlatformSideNav]);

  const adaptedSecondaryNavItems = useMemo(() => {
    const updatedItems = isPlatformSideNav
      ? navigation.secondary?.filter((item) => item.type !== 'USER_MENU')
      : navigation.secondary;
    return removeUnsupportedItemsFromGroup(updatedItems ?? (EMPTY_ARRAY as NavItems));
  }, [navigation.secondary, isPlatformSideNav]);

  const userMenuItem = useMemo(() => {
    return navigation.secondary?.find((item) => item.type === 'USER_MENU');
  }, [navigation.secondary]);

  const slotProps = useMemo(
    () => ({
      navigationItem: {
        labelVariant,
        selectionVariant,
        position,
        resolution,
        expandableNavVariant,
        direction,
        expanded,
        setExpanded,
        alignment,
        typographyClassName,
        icon,
        navItemClassName,
        navItemStyles,
        navItemBorderRadiusClassName,
        navItemBorderRadiusStyles,
        hideIcon: false,
        hideActionNavLabel: isTopPositioned,
        expandedGroupId,
        setExpandedGroupId,
        navItemPopover,
        setNavItemPopover,
      },
      borderColorClassName,
      theme,
    }),
    [
      labelVariant,
      selectionVariant,
      position,
      resolution,
      expandableNavVariant,
      direction,
      expanded,
      setExpanded,
      alignment,
      typographyClassName,
      icon,
      navItemClassName,
      navItemStyles,
      navItemBorderRadiusClassName,
      navItemBorderRadiusStyles,
      borderColorClassName,
      theme,
      isTopPositioned,
      expandedGroupId,
      navItemPopover,
      setNavItemPopover,
    ],
  );

  const { primaryNavRef, secondaryNavRef, isOverlapping } = useOverlapObserver({
    isPlatformSideNav,
  });

  const isHorizontal = direction === NavigationItemDirection.Row;

  const secondaryLength = adaptedSecondaryNavItems.length;
  const primaryLength = adaptedPrimaryNavItems.length;

  // Platform root nav items use an 8px gap; nested item spacing is handled inside NavigationGroup.
  const secondaryMinHeight = secondaryLength * 36 + (secondaryLength - 1) * 8 + 24 + 2;

  const primaryMinHeight =
    primaryLength * 36 + (primaryLength - 1) * 8 + 16 + (isOverlapping ? 2 : 0);

  const secondaryNavStyles = useMemo<CSSProperties>(
    () => ({
      ...navItemsGapStyles,
      ...(isPlatformSideNav ? { minHeight: secondaryMinHeight } : {}),
      ...(isOverlapping ? UA_PLATFORM_SIDE_NAV_OVERLAPPING_STYLES : {}),
      ...(isOverlapping && isPlatformSideNav ? { borderTop: '2px solid transparent' } : {}),
    }),
    [isOverlapping, isPlatformSideNav, navItemsGapStyles, secondaryMinHeight],
  );

  const primaryNavStyles = useMemo<CSSProperties>(() => {
    return {
      ...navItemsGapStyles,
      ...(isPlatformSideNav ? { minHeight: primaryMinHeight } : {}),
    };
  }, [isPlatformSideNav, navItemsGapStyles, primaryMinHeight]);

  return {
    isHorizontalUpfront,
    isHorizontal,
    isLabelExpandable,
    isUserNavItemPresent,
    brandNavigationItem,
    adaptedPrimaryNavItems,
    adaptedSecondaryNavItems,
    slotProps,
    rootClassName,
    className,
    style,
    hoverProps,
    expanded,
    setExpanded,
    activeMenuItemId,
    setActiveMenuItemId,
    selectionVariant,
    navItemsGapClassName,
    app,
    activePathnames,
    navigation,
    position,
    isPlatformSideNav,
    isTopPositioned,
    primaryNavRef,
    secondaryNavRef,
    secondaryNavStyles,
    primaryNavStyles,
    labelVariant,
    userMenuItem,
  };
}
